import React, { useState, useEffect, useRef, useMemo } from "react";

export interface ActionButtonConfig {
  variant:
    | "primary"
    | "secondary"
    | "success"
    | "danger"
    | "warning"
    | "info"
    | "primary-outline"
    | "secondary-outline"
    | "success-outline"
    | "danger-outline"
    | "warning-outline"
    | "info-outline"
    | "danger-text"
    | "ghost";
  action: string;
  label: string;
  icon?: React.ReactNode;
  priority?: number;
  hideOnMobile?: boolean;
  tooltip?: string;
  shortLabel?: string;
  description?: string;
  isNew?: boolean;
  visualEffectsLevel?: "minimal" | "default" | "fancy";
}

interface ActionButtonsProps {
  buttonsConfig: ActionButtonConfig[];
  onAction: (action: string) => void;
  size?: "xs" | "sm" | "md" | "lg";
  disabled?: boolean;
  loadingAction?: string | null;
  className?: string;
  adaptiveMode?: "conservative" | "balanced" | "aggressive";
  layout?: "default" | "grouped" | "segmented" | "spaced";
  loadingSpinner?: React.ReactNode;
  loadingText?: string;
  containerStyle?: "floating" | "plain" | "material" | "glass";
  animationLevel?: "minimal" | "default" | "fancy";
  colorScheme?: "brand" | "pastel" | "vibrant" | "monochrome";
  displayMode?: "all" | "dropdown-only" | "adaptive"; // New display mode prop
  useDropdown?: boolean; // Kept for backward compatibility
  dropdownDirection?: "down" | "up" | "left" | "right";
  dropdownType?: "simple" | "menu" | "popover";
  dropdownMaxItems?: number; // Control how many items show before overflow to dropdown
}

/**
 * A responsive action buttons component that intelligently adapts to available space.
 * Buttons can be displayed in full or placed in a dropdown menu when space is limited.
 * The component includes smart positioning for the dropdown based on available screen space.
 */
const ActionButtons: React.FC<ActionButtonsProps> = ({
  buttonsConfig,
  onAction,
  size = "sm",
  disabled = false,
  loadingAction = null,
  className = "",
  adaptiveMode = "balanced",
  layout = "default",
  loadingSpinner,
  loadingText = "Loading...",
  containerStyle = "plain",
  animationLevel = "default",
  colorScheme = "brand",
  displayMode = "adaptive", // Default to adaptive mode
  useDropdown, // For backward compatibility
  dropdownDirection = "down",
  dropdownType = "menu",
  dropdownMaxItems = 3, // Default to showing 3 items before overflow
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const [animationReady, setAnimationReady] = useState<boolean>(false);
  const [showLabels, setShowLabels] = useState<boolean>(true);
  const [isCondensed, setIsCondensed] = useState<boolean>(false);
  const [availableWidth, setAvailableWidth] = useState<number>(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [activeButtonIndex, setActiveButtonIndex] = useState<number>(-1);

  // Find parent element for size monitoring and setup initial state
  useEffect(() => {
    if (!containerRef.current) return;

    // Enable animations after initial render to prevent initial transition flashes
    setTimeout(() => {
      setAnimationReady(true);
    }, 50);
  }, []);

  // Calculate animation duration based on level
  const animationDuration = useMemo(() => {
    return animationLevel === "minimal"
      ? 100
      : animationLevel === "fancy"
        ? 300
        : 200;
  }, [animationLevel]);

  // Calculate space thresholds based on adaptive mode
  const adaptiveThresholds = useMemo(() => {
    const base = {
      widthToStartCondensing: 600, // px
      widthToSwitchToIconOnly: 350, // px
      minWidthPerButton: 90, // px
      minWidthPerIconButton: 36, // px
    };

    // Adjust thresholds based on adaptive mode
    switch (adaptiveMode) {
      case "conservative":
        return {
          ...base,
          widthToStartCondensing: 700,
          widthToSwitchToIconOnly: 450,
          minWidthPerButton: 110,
          minWidthPerIconButton: 42,
        };
      case "aggressive":
        return {
          ...base,
          widthToStartCondensing: 500,
          widthToSwitchToIconOnly: 280,
          minWidthPerButton: 80,
          minWidthPerIconButton: 32,
        };
      default: // balanced
        return base;
    }
  }, [adaptiveMode]);

  // Sort buttons by priority (if specified)
  const sortedButtons = useMemo(() => {
    return [...buttonsConfig].sort((a, b) => {
      const priorityA = a.priority || 0;
      const priorityB = b.priority || 0;
      return priorityB - priorityA;
    });
  }, [buttonsConfig]);

  // Determine button sizes based on the 'size' prop
  const buttonSizes = useMemo(() => {
    switch (size) {
      case "xs":
        return {
          button: "px-2 py-1 text-xs",
          iconOnly: "p-1",
          minButtonWidth: 70,
          minIconButtonWidth: 26,
          spaceBetween: "gap-1",
          iconSize: 14,
        };
      case "sm":
        return {
          button: "px-3 py-1.5 text-sm",
          iconOnly: "p-1.5",
          minButtonWidth: 85,
          minIconButtonWidth: 32,
          spaceBetween: "gap-2",
          iconSize: 16,
        };
      case "md":
        return {
          button: "px-4 py-2 text-base",
          iconOnly: "p-2",
          minButtonWidth: 100,
          minIconButtonWidth: 38,
          spaceBetween: "gap-2",
          iconSize: 18,
        };
      case "lg":
        return {
          button: "px-5 py-2.5 text-lg",
          iconOnly: "p-2.5",
          minButtonWidth: 120,
          minIconButtonWidth: 46,
          spaceBetween: "gap-3",
          iconSize: 20,
        };
      default:
        return {
          button: "px-3 py-1.5 text-sm",
          iconOnly: "p-1.5",
          minButtonWidth: 85,
          minIconButtonWidth: 32,
          spaceBetween: "gap-2",
          iconSize: 16,
        };
    }
  }, [size]);

  // Get container style classes
  const containerStyles = useMemo(() => {
    switch (containerStyle) {
      case "floating":
        return "bg-white/90 backdrop-blur-sm shadow-md rounded-lg px-2 py-1.5";
      case "material":
        return "bg-white shadow-sm border rounded-md px-1 py-0.5";
      case "glass":
        return "bg-white/20 backdrop-blur-lg border border-white/30 rounded-xl px-2 py-1.5";
      case "plain":
      default:
        return "";
    }
  }, [containerStyle]);

  // Determines layout styling
  const layoutStyles = useMemo(() => {
    switch (layout) {
      case "grouped":
        return "inline-flex rounded-md shadow-sm";
      case "segmented":
        return "inline-flex divide-x divide-gray-200 border border-gray-200 rounded-md overflow-hidden";
      case "spaced":
        return `flex flex-wrap ${buttonSizes.spaceBetween}`;
      case "default":
      default:
        return `flex flex-wrap ${buttonSizes.spaceBetween}`;
    }
  }, [layout, buttonSizes.spaceBetween]);

  // Track available width from parent container
  useEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = () => {
      if (containerRef.current) {
        const newWidth = containerRef.current.parentElement?.clientWidth || 0;
        if (newWidth !== availableWidth) {
          setAvailableWidth(newWidth);

          // Calculate if we need to show condensed view based on available width
          const totalButtonsWidth =
            sortedButtons.length * adaptiveThresholds.minWidthPerButton;
          const useCondensed = newWidth < totalButtonsWidth;

          // We no longer use icon-only mode - we'll use dropdown instead when space is limited
          setShowLabels(true);
          setIsCondensed(useCondensed);
        }
      }
    };

    // Initial measurement
    updateWidth();

    // Set up resize observers
    const resizeObserver = new ResizeObserver(updateWidth);

    if (containerRef.current.parentElement) {
      resizeObserver.observe(containerRef.current.parentElement);
    }

    // Also observe the container itself
    resizeObserver.observe(containerRef.current);

    // Cleanup
    return () => {
      resizeObserver.disconnect();
    };
  }, [availableWidth, adaptiveThresholds, sortedButtons.length]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        isDropdownOpen
      ) {
        setIsDropdownOpen(false);
      }
    };

    // Also close on scroll or resize to prevent positioning issues
    const handleWindowEvents = () => {
      if (isDropdownOpen) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleWindowEvents);
    window.addEventListener("resize", handleWindowEvents);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleWindowEvents);
      window.removeEventListener("resize", handleWindowEvents);
    };
  }, [isDropdownOpen]);

  // Get variant styles with color scheme support
  const getVariantStyles = (
    variant: string,
    isLoading: boolean,
    isHovered: boolean = false
  ) => {
    const baseStyles =
      "transition-all duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-offset-1";
    const disabledStyles = "opacity-60 cursor-not-allowed";

    // Base variant styles
    const baseVariants: Record<string, string> = {
      primary: [
        "bg-gradient-to-r",
        "from-primary-light",
        "to-primary",
        "text-text-on-primary",
        "hover:from-primary",
        "hover:to-primary-dark",
        "focus:ring-primary",
      ].join(" "),
      secondary: [
        "bg-gradient-to-r",
        "from-secondary-light",
        "to-secondary",
        "text-text-on-primary",
        "hover:from-secondary",
        "hover:to-secondary-dark",
        "focus:ring-secondary",
      ].join(" "),
      success: [
        "bg-gradient-to-r",
        "from-success-light",
        "to-success",
        "text-text-on-primary",
        "hover:from-success",
        "hover:to-success-dark",
        "focus:ring-success",
      ].join(" "),
      danger: [
        "bg-gradient-to-r",
        "from-danger-light",
        "to-danger",
        "text-text-on-primary",
        "hover:from-danger",
        "hover:to-danger-dark",
        "focus:ring-danger",
      ].join(" "),
      warning: [
        "bg-gradient-to-r",
        "from-warning-light",
        "to-warning",
        "text-text-on-primary",
        "hover:from-warning",
        "hover:to-warning-dark",
        "focus:ring-warning",
      ].join(" "),
      info: [
        "bg-gradient-to-r",
        "from-secondary-light",
        "to-secondary",
        "text-text-on-primary",
        "hover:from-secondary",
        "hover:to-secondary-dark",
        "focus:ring-secondary",
      ].join(" "),
      ghost: [
        "bg-transparent",
        "hover:bg-neutral-light",
        "text-text",
        "focus:ring-primary",
      ].join(" "),
      "primary-outline": [
        "border",
        "border-primary",
        "text-primary",
        "bg-transparent",
        "hover:bg-primary",
        "hover:text-text-on-primary",
        "focus:ring-primary",
      ].join(" "),
      "secondary-outline": [
        "border",
        "border-secondary",
        "text-secondary",
        "bg-transparent",
        "hover:bg-secondary",
        "hover:text-text-on-primary",
        "focus:ring-secondary",
      ].join(" "),
      "success-outline": [
        "border",
        "border-success",
        "text-success",
        "bg-transparent",
        "hover:bg-success",
        "hover:text-text-on-primary",
        "focus:ring-success",
      ].join(" "),
      "danger-outline": [
        "border",
        "border-danger",
        "text-danger",
        "bg-transparent",
        "hover:bg-danger",
        "hover:text-text-on-primary",
        "focus:ring-danger",
      ].join(" "),
      "warning-outline": [
        "border",
        "border-warning",
        "text-warning",
        "bg-transparent",
        "hover:bg-warning",
        "hover:text-text-on-primary",
        "focus:ring-warning",
      ].join(" "),
      "info-outline": [
        "border",
        "border-secondary",
        "text-secondary",
        "bg-transparent",
        "hover:bg-secondary",
        "hover:text-text-on-primary",
        "focus:ring-secondary",
      ].join(" "),
      "danger-text":
        "text-danger hover:text-danger-dark hover:bg-danger-light focus:ring-danger",
    };

    // Adjust colors based on color scheme
    let variantMap = { ...baseVariants };
    if (colorScheme === "pastel") {
      variantMap = {
        ...variantMap,
        primary: [
          "bg-gradient-to-r",
          "from-indigo-light",
          "to-indigo",
          "text-text-on-primary",
          "hover:from-indigo",
          "hover:to-indigo-dark",
          "focus:ring-indigo",
        ].join(" "),
        success: [
          "bg-gradient-to-r",
          "from-green-light",
          "to-green",
          "text-text-on-primary",
          "hover:from-green",
          "hover:to-green-dark",
          "focus:ring-green",
        ].join(" "),
        danger: [
          "bg-gradient-to-r",
          "from-pink-light",
          "to-pink",
          "text-text-on-primary",
          "hover:from-pink",
          "hover:to-pink-dark",
          "focus:ring-pink",
        ].join(" "),
      };
    } else if (colorScheme === "vibrant") {
      variantMap = {
        ...variantMap,
        primary: [
          "bg-gradient-to-r",
          "from-violet-light",
          "to-violet",
          "text-text-on-primary",
          "hover:from-violet",
          "hover:to-violet-dark",
          "focus:ring-violet",
        ].join(" "),
        success: [
          "bg-gradient-to-r",
          "from-emerald-light",
          "to-emerald",
          "text-text-on-primary",
          "hover:from-emerald",
          "hover:to-emerald-dark",
          "focus:ring-emerald",
        ].join(" "),
        danger: [
          "bg-gradient-to-r",
          "from-rose-light",
          "to-rose",
          "text-text-on-primary",
          "hover:from-rose",
          "hover:to-rose-dark",
          "focus:ring-rose",
        ].join(" "),
      };
    } else if (colorScheme === "monochrome") {
      variantMap = {
        ...variantMap,
        primary: [
          "bg-gradient-to-r",
          "from-gray-800",
          "to-gray-900",
          "text-text-on-primary",
          "hover:from-gray-900",
          "hover:to-black",
          "focus:ring-gray-700",
        ].join(" "),
        secondary: [
          "bg-gradient-to-r",
          "from-gray-200",
          "to-gray-300",
          "text-text-on-primary",
          "hover:from-gray-300",
          "hover:to-gray-400",
          "focus:ring-gray-400",
        ].join(" "),
        success: [
          "bg-gradient-to-r",
          "from-gray-700",
          "to-gray-800",
          "text-text-on-primary",
          "hover:from-gray-800",
          "hover:to-gray-900",
          "focus:ring-gray-600",
        ].join(" "),
        danger: [
          "bg-gradient-to-r",
          "from-gray-900",
          "to-black",
          "text-text-on-primary",
          "hover:from-black",
          "hover:to-gray-800",
          "focus:ring-gray-700",
        ].join(" "),
      };
    }

    // Handle special layout modes
    if (layout === "segmented") {
      return `${baseStyles} ${variantMap[variant] || variantMap["secondary"]} rounded-none ${isLoading || disabled ? disabledStyles : ""}`;
    }

    return `${baseStyles} ${variantMap[variant] || variantMap["secondary"]} ${isLoading || disabled ? disabledStyles : ""}`;
  };

  // Get animation classes based on animation level and ready state
  const getAnimationClasses = () => {
    if (!animationReady) return "";

    switch (animationLevel) {
      case "minimal":
        return "transition-opacity duration-100";
      case "fancy":
        return "transition-all duration-300 ease-in-out";
      case "default":
      default:
        return "transition-all duration-200";
    }
  };

  const getDropdownPositionStyles = () => {
    // Get component dimensions and position
    const containerRect = containerRef.current?.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isDropdownOnlyMode = displayMode === "dropdown-only";

    // For small screens, we handle positioning via JS after render
    if (viewportWidth < 640) {
      return "";
    }

    // Default to down direction
    let direction = dropdownDirection;

    // Auto-detect best direction based on available space
    if (containerRect) {
      const spaceBelow = viewportHeight - containerRect.bottom;
      const spaceAbove = containerRect.top;
      const spaceRight = viewportWidth - containerRect.right;
      const spaceLeft = containerRect.left;
      const dropdownWidth = Math.max(220, containerRect.width); // Use at least 220px or container width
      const estimatedDropdownHeight = dropdownButtons.length * 40 + 20; // Estimate height based on items

      // First check vertical space
      if (
        direction === "down" &&
        spaceBelow < estimatedDropdownHeight &&
        spaceAbove > spaceBelow
      ) {
        direction = "up";
      } else if (
        direction === "up" &&
        spaceAbove < estimatedDropdownHeight &&
        spaceBelow > spaceAbove
      ) {
        direction = "down";
      }

      // Horizontal alignment - special case for dropdown-only mode
      if (isDropdownOnlyMode) {
        // For dropdown-only mode, we want to align centered with the button
        return `${direction === "up" ? "bottom-full mb-0.5" : "top-full mt-0.5"} left-1/2 -translate-x-1/2`;
      }

      // Standard horizontal alignment based on available space
      let horizontalAlignment = "";
      if (spaceRight < dropdownWidth) {
        horizontalAlignment = "right-0";
      } else if (containerRect.right > viewportWidth / 2) {
        horizontalAlignment = "right-0";
      } else {
        horizontalAlignment = "left-0";
      }

      return `${direction === "up" ? "bottom-full mb-1" : "top-full mt-1"} ${horizontalAlignment}`;
    }

    // Default positioning
    return isDropdownOnlyMode
      ? `${direction === "up" ? "bottom-full mb-0.5" : "top-full mt-0.5"} left-1/2 -translate-x-1/2`
      : `${direction === "up" ? "bottom-full mb-1" : "top-full mt-1"} right-0`;
  };

  // Get dropdown styling based on dropdown type
  const getDropdownStyles = () => {
    const positionStyles = getDropdownPositionStyles();
    const isSmallScreen = window.innerWidth < 640;

    // Base styles for all screens
    let baseStyles = isSmallScreen
      ? "bg-white shadow-xl p-0 border border-gray-100 rounded-lg" // For small screens, positioning handled by JS
      : `absolute z-20 ${positionStyles} min-w-[220px] rounded-md overflow-hidden`; // For large screens

    // Apply appropriate styling based on screen size and type
    if (isSmallScreen) {
      return `${baseStyles} max-h-[80vh] overflow-y-auto`;
    }

    // Dropdown type styling for normal screens
    switch (dropdownType) {
      case "popover":
        return `${baseStyles} bg-white ring-1 ring-black ring-opacity-5 shadow-lg p-1 border border-gray-200`;
      case "menu":
        return `${baseStyles} bg-white shadow-xl p-1 border border-gray-100 rounded-lg`;
      case "simple":
      default:
        return `${baseStyles} bg-white shadow-md ring-1 ring-black ring-opacity-5 p-1`;
    }
  };

  const { visibleButtons, dropdownButtons } = useMemo(() => {
    // Handle backward compatibility with useDropdown prop
    const effectiveDisplayMode =
      useDropdown !== undefined
        ? useDropdown
          ? "dropdown"
          : "adaptive"
        : displayMode;

    // Case 1: Show all actions in the dropdown for small screens
    if (window.innerWidth < 640) {
      // On small screens, always use dropdown for all buttons except the highest priority one
      const highestPriorityButton =
        sortedButtons.length > 0 ? [sortedButtons[0]] : [];
      return {
        visibleButtons: highestPriorityButton,
        dropdownButtons: sortedButtons.slice(1),
      };
    }

    // Case 2: Show all actions in the dropdown (by configuration)
    if (effectiveDisplayMode === "dropdown-only") {
      return {
        visibleButtons: [],
        dropdownButtons: sortedButtons,
      };
    }

    // Case 3: Adaptive mode - automatically adjust based on available space
    if (effectiveDisplayMode === "adaptive") {
      const parentWidth = containerRef.current?.parentElement?.clientWidth || 0;
      const minButtonWidth =
        buttonSizes.minButtonWidth || adaptiveThresholds.minWidthPerButton;
      const maxButtonsToShow = Math.max(
        0,
        Math.floor((parentWidth - 50) / minButtonWidth) // Reserve space for dropdown button
      );

      // If we have very limited space, use dropdown for all buttons
      if (maxButtonsToShow === 0) {
        return {
          visibleButtons: [],
          dropdownButtons: sortedButtons,
        };
      }

      // Otherwise, fit as many buttons as possible
      const numButtonsToShow = Math.min(
        maxButtonsToShow,
        dropdownMaxItems,
        sortedButtons.length
      );
      return {
        visibleButtons: sortedButtons.slice(0, numButtonsToShow),
        dropdownButtons: sortedButtons.slice(numButtonsToShow),
      };
    }

    // Default case: show all buttons directly if explicitly requested with displayMode="all"
    return {
      visibleButtons: sortedButtons,
      dropdownButtons: [],
    };
  }, [
    sortedButtons,
    displayMode,
    useDropdown,
    adaptiveThresholds.minWidthPerButton,
    availableWidth,
    dropdownMaxItems,
    buttonSizes.minButtonWidth,
  ]);

  useEffect(() => {
    // When dropdown opens, position it correctly based on screen size
    if (isDropdownOpen && dropdownRef.current) {
      const buttonRect = containerRef.current?.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const isSmallScreen = viewportWidth < 640;
      const isDropdownOnlyMode = displayMode === "dropdown-only";

      if (buttonRect) {
        // Clear previous inline styles that might conflict
        if (!isSmallScreen) {
          dropdownRef.current.style.position = "";
          dropdownRef.current.style.top = "";
          dropdownRef.current.style.bottom = "";
          dropdownRef.current.style.left = "";
          dropdownRef.current.style.right = "";
          dropdownRef.current.style.transform = "";
        }

        if (isSmallScreen) {
          // Mobile positioning as bottom sheet
          dropdownRef.current.style.position = "fixed";
          dropdownRef.current.style.zIndex = "50";
          dropdownRef.current.style.left = "0";
          dropdownRef.current.style.right = "0";
          dropdownRef.current.style.width = "100%";
          dropdownRef.current.style.bottom = "0";
          dropdownRef.current.style.top = "auto";
          dropdownRef.current.style.maxHeight = "80vh";
          dropdownRef.current.style.borderBottomLeftRadius = "0";
          dropdownRef.current.style.borderBottomRightRadius = "0";
          dropdownRef.current.style.borderTopLeftRadius = "12px";
          dropdownRef.current.style.borderTopRightRadius = "12px";
        } else if (isDropdownOnlyMode) {
          // For dropdown-only mode on larger screens, ensure it's directly under the button
          const dropdownRect = dropdownRef.current.getBoundingClientRect();

          // Position dropdown relative to the button
          dropdownRef.current.style.position = "absolute";
          dropdownRef.current.style.zIndex = "20";

          // Position directly below button
          dropdownRef.current.style.top = "100%";
          dropdownRef.current.style.marginTop = "4px";

          // Center horizontally
          dropdownRef.current.style.left = "50%";
          dropdownRef.current.style.transform = "translateX(-50%)";

          // Check if dropdown would go off bottom of screen
          const bottomOverflow =
            buttonRect.bottom + dropdownRect.height - viewportHeight;
          if (bottomOverflow > 0) {
            // Position above button instead
            dropdownRef.current.style.top = "auto";
            dropdownRef.current.style.bottom = "100%";
            dropdownRef.current.style.marginBottom = "4px";
            dropdownRef.current.style.marginTop = "0";
          }
        } else {
          // Standard positioning for adaptive mode
          const dropdownRect = dropdownRef.current.getBoundingClientRect();

          // Check if dropdown extends beyond viewport edges and adjust if needed
          if (dropdownRect.right > viewportWidth) {
            dropdownRef.current.style.right = "0";
            dropdownRef.current.style.left = "auto";
          }

          if (dropdownRect.bottom > viewportHeight) {
            dropdownRef.current.style.bottom = "100%";
            dropdownRef.current.style.top = "auto";
            dropdownRef.current.style.marginBottom = "4px";
            dropdownRef.current.style.marginTop = "0";
          }
        }

        // Always ensure dropdown is scrollable if it's large
        dropdownRef.current.style.overflowY = "auto";
      }
    }
  }, [isDropdownOpen, displayMode, dropdownButtons.length, dropdownDirection]);

  // Determine button rounded style based on layout
  const getButtonRoundedStyle = (index: number, isMore: boolean = false) => {
    if (layout === "segmented") return "";
    if (layout === "grouped") {
      if (index === 0 && sortedButtons.length === 1) return "rounded-md";
      if (index === 0) return "rounded-l-md";
      if (index === sortedButtons.length - 1) return "rounded-r-md";
      return "";
    }
    return "rounded-md";
  };

  return (
    <div
      ref={containerRef}
      className={`inline-flex flex-wrap ${containerStyles} ${className}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      role="toolbar"
      aria-label="Action buttons"
    >
      <div className={layoutStyles}>
        {/* Visible buttons */}
        {visibleButtons.map((button, index) => {
          const isLoading = loadingAction === button.action;
          const buttonRoundedStyle = getButtonRoundedStyle(index);

          // Determine label to show
          let displayLabel = button.label;
          if (isCondensed && button.shortLabel) {
            displayLabel = button.shortLabel;
          }

          return (
            <button
              key={button.action}
              type="button"
              onClick={() => onAction(button.action)}
              disabled={disabled || isLoading}
              className={`
              relative ${getVariantStyles(button.variant, isLoading, isHovering)} 
              ${buttonRoundedStyle}
              ${buttonSizes.button}
              ${getAnimationClasses()}
              ${layout === "segmented" ? "-ml-px first:ml-0" : ""}
              ${layout === "grouped" ? "first:border-r-0 last:border-l-0 -ml-px first:ml-0" : ""}
              flex items-center justify-center whitespace-nowrap
            `}
              title={button.tooltip}
              aria-label={button.label}
              style={{
                transition: `all ${animationDuration}ms`,
              }}
            >
              {isLoading ? (
                <>
                  {loadingSpinner || (
                    <span
                      className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full"
                      aria-hidden="true"
                    ></span>
                  )}
                  {displayLabel && <span className="ml-2">{loadingText}</span>}
                </>
              ) : (
                <>
                  {button.icon && <span className="mr-2">{button.icon}</span>}
                  {displayLabel && <span>{displayLabel}</span>}

                  {/* New badge indicator if needed */}
                  {button.isNew && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full flex items-center justify-center text-[9px] text-white font-bold">
                      !
                    </span>
                  )}
                </>
              )}
            </button>
          );
        })}

        {/* Dropdown menu if we have overflow items */}
        {dropdownButtons.length > 0 && (
          <div ref={dropdownRef} className="relative inline-flex">
            <button
              type="button"
              className={`
              ${getVariantStyles("secondary", false)} 
              ${getButtonRoundedStyle(visibleButtons.length, true)}
              ${buttonSizes.button}
              ${getAnimationClasses()}
              ${layout === "segmented" ? "-ml-px" : ""}
              ${layout === "grouped" ? "-ml-px" : ""}
              flex items-center justify-center
            `}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              aria-expanded={isDropdownOpen}
              aria-haspopup="menu"
              aria-controls="action-dropdown"
              title="More actions"
            >
              {/* Three dots icon with label "More" for dropdown-only mode */}
              {displayMode === "dropdown-only" ? (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mr-1"
                  >
                    <circle cx="12" cy="12" r="1"></circle>
                    <circle cx="19" cy="12" r="1"></circle>
                    <circle cx="5" cy="12" r="1"></circle>
                  </svg>
                  <span>Actions</span>
                </>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="1"></circle>
                  <circle cx="19" cy="12" r="1"></circle>
                  <circle cx="5" cy="12" r="1"></circle>
                </svg>
              )}
            </button>

            {isDropdownOpen && (
              <div
                ref={dropdownRef}
                id="action-dropdown"
                className={`${getDropdownStyles()} dropdown-panel`}
                role="menu"
                aria-orientation="vertical"
              >
                {/* Mobile screen header for dropdown-only mode */}
                {window.innerWidth < 640 && displayMode === "dropdown-only" && (
                  <div className="px-3 py-2 border-b border-gray-200 font-medium sticky top-0 bg-white">
                    Actions
                  </div>
                )}

                {dropdownButtons.map((button, index) => {
                  const isLoading = loadingAction === button.action;
                  const isActive = index === activeButtonIndex;

                  return (
                    <div
                      key={button.action}
                      role="menuitem"
                      tabIndex={0}
                      className={`
                      ${isActive ? "bg-gray-100" : ""}
                      px-3 py-2 text-sm
                      flex items-center rounded-md cursor-pointer
                      ${isLoading || disabled ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-100"}
                      transition-colors duration-150
                      ${window.innerWidth < 640 ? "py-4" : ""} 
                    `}
                      onClick={() => {
                        if (!isLoading && !disabled) {
                          onAction(button.action);
                          setIsDropdownOpen(false);
                        }
                      }}
                      onMouseEnter={() => setActiveButtonIndex(index)}
                      onMouseLeave={() => setActiveButtonIndex(-1)}
                    >
                      {isLoading ? (
                        <span className="animate-spin mr-3 inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full"></span>
                      ) : (
                        button.icon && (
                          <span
                            className={`mr-3 ${button.variant.includes("danger") ? "text-red-500" : "text-gray-500"}`}
                          >
                            {button.icon}
                          </span>
                        )
                      )}

                      <div className="flex flex-col">
                        <span
                          className={
                            button.variant.includes("danger")
                              ? "text-red-600"
                              : ""
                          }
                        >
                          {button.label}
                        </span>

                        {button.description && (
                          <span className="text-gray-500 text-xs mt-0.5">
                            {button.description}
                          </span>
                        )}
                      </div>

                      {button.isNew && (
                        <span className="ml-auto bg-red-100 text-red-800 text-xs px-1.5 py-0.5 rounded-full font-medium">
                          New
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionButtons;
